import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import * as sectionBlockActions from 'action/sectionBlock'
import Select from '../Select/Select'
import Api from 'utils/ApiRequest'
import API_URL from 'constants/api'

const FORM_INITIAL_STATE = {
  values: {
    stores: [],
    sections: [],
    subsections: [],
    types: [],
    subtypes: [],
  },
  options: {
    sections: [],
    subsections: [],
    types: [],
    subtypes: [],
  },
  activeStores: null,
}

const DEFAULT_API_PARAMS = {
  level: 2,
  sectionId: 0,
  subSectionId: 0,
  typeId: 0,
  subTypeId: 0,
  limit: 50,
  offset: 0,
}

class NewSectionBlockForm extends Component {
  static propTypes = {
    translate: PropTypes.func.isRequired,
    storeList: PropTypes.array,
    options: PropTypes.any,
    auth: PropTypes.object,
    onChange: PropTypes.func,
    excludeInactive: PropTypes.bool,
  }

  state = FORM_INITIAL_STATE

  async componentDidMount() {
    const { excludeInactive, auth } = this.props
    if (excludeInactive && auth?.user) {
      try {
        const { data } = await new Api(auth.user.dsToken).post(`${API_URL.BRANCHES}?excludeInactive=true`)
        this.setState({ activeStores: data })
      } catch (error) {
        this.setState({ activeStores: [] })
      }
    }
  }

  callApi = (token, params) => {
    this.setState({ loading: true })

    return new Api(token).post(API_URL.SECTIONS_HIERARCHY, params)
  }

  triggerChange = () => {
    const { onChange } = this.props
    const { values, options } = this.state
    onChange &&
      onChange(
        {
          stores: values.stores.map(({ value }) => +value),
          sections: values.sections.map(({ value = {} }) => +value.sectionId),
          subsections: values.subsections.map(
            ({ value = {} }) => +value.subSectionId
          ),
          types: values.types.map(({ value = {} }) => +value.typeId),
          subtypes: values.subtypes.map(({ value = {} }) => +value.subTypeId),
        },
        { ...options }
      )
  }

  handleSelectChange = async (level, relatedKey, idKey, paramsList) => {
    const { options = {} } = this.state
    const {
      auth: { user },
    } = this.props

    this.setState(
      {
        options: {
          ...options,
          [relatedKey]: [],
        },
      },
      () => {
        paramsList.forEach(async (params) => {
          const { sectionId, subSectionId, typeId } = params

          const { data } = await this.callApi(user.dsToken, {
            ...DEFAULT_API_PARAMS,
            level: level || DEFAULT_API_PARAMS.level,
            sectionId: sectionId || DEFAULT_API_PARAMS.sectionId,
            subSectionId: subSectionId || DEFAULT_API_PARAMS.subSectionId,
            typeId: typeId || DEFAULT_API_PARAMS.typeId,
          })

          const existingIds = (options[relatedKey] || []).map((i) => i[idKey])
          const newValue = [
            ...(options[relatedKey] || []),
            ...data.filter((item) => {
              if (idKey) {
                const shouldInclude = existingIds.indexOf(item[idKey]) === -1
                if (shouldInclude) {
                  existingIds.push(item[idKey])
                }
                return shouldInclude
              }
              return true
            }),
          ]

          this.setState(
            {
              options: {
                ...options,
                [relatedKey]: newValue,
              },
            },
            () => {
              this.triggerChange()
            }
          )
        })
        if ((paramsList || []).length === 0) {
          this.setState(
            {
              options: {
                ...options,
                [relatedKey]: [],
              },
            },
            () => {
              this.triggerChange()
            }
          )
        }
      }
    )
  }

  render() {
    const { storeList = [], translate, excludeInactive, options: propsOptions } = this.props
    const { values, options, activeStores } = this.state

    const stores = excludeInactive 
      ? (activeStores || []) 
      : (storeList || propsOptions?.stores || [])

    return (
      <form onSubmit={this.beforeHandleSubmit} style={{ width: '100%' }}>
        <Select
          label={translate('newSectionBlock.form.storeLabel')}
          placeholder={translate('newSectionBlock.form.storePlaceholder')}
          required
          validateValue={(value) => !!(value || []).length}
          options={stores.map((store) => ({
            value: store.id,
            label: `${store.id} - ${store.name}`,
          }))}
          isMulti
          groupOptions
          selectAll
          translate={translate}
          value={values.stores}
          onChange={(vls) =>
            this.setState({ values: { ...values, stores: vls } }, () => {
              this.handleSelectChange(2, 'sections', 'sectionId', [{}])
            })
          }
          fill
        />
        <Select
          label={translate('newSectionBlock.form.sectionLabel')}
          placeholder={translate('newSectionBlock.form.sectionPlaceholder')}
          required
          validateValue={(value) => !!(value || []).length}
          options={options.sections.map((item) => ({
            value: item,
            label: item.description,
          }))}
          isMulti
          groupOptions
          selectAll
          translate={translate}
          value={values.sections}
          onChange={(vls) => {
            this.setState(
              {
                values: {
                  ...values,
                  sections: vls,
                  subsections: [],
                  types: [],
                  subtypes: [],
                },
                options: {
                  ...options,
                  subsections: vls.length ? options.subsections : [],
                  types: vls.length ? options.types : [],
                  subtypes: vls.length ? options.subtypes : [],
                },
              },
              () => {
                this.handleSelectChange(
                  3,
                  'subsections',
                  null,
                  vls.map(({ value: { sectionId } }) => ({ sectionId }))
                )
              }
            )
          }}
          fill
        />
        <Select
          label={translate('newSectionBlock.form.subsectionLabel')}
          placeholder={translate('newSectionBlock.form.subsectionPlaceholder')}
          required
          validateValue={(value) => !!(value || []).length}
          options={options.subsections.map((item) => ({
            value: item,
            label: item.description,
          }))}
          isMulti
          groupOptions
          selectAll
          translate={translate}
          value={values.subsections}
          onChange={(vls) =>
            this.setState(
              {
                values: {
                  ...values,
                  subsections: vls,
                  types: [],
                  subtypes: [],
                },
                options: {
                  ...options,
                  types: vls.length ? options.types : [],
                  subtypes: vls.length ? options.subtypes : [],
                },
              },
              () => {
                this.handleSelectChange(
                  4,
                  'types',
                  null,
                  vls.map(({ value: { sectionId, subSectionId } }) => ({
                    sectionId,
                    subSectionId,
                  }))
                )
              }
            )
          }
          fill
        />
        <Select
          label={translate('newSectionBlock.form.typeLabel')}
          placeholder={translate('newSectionBlock.form.typePlaceholder')}
          required
          validateValue={(value) => !!(value || []).length}
          options={options.types.map((item) => ({
            value: item,
            label: item.description,
          }))}
          isMulti
          groupOptions
          selectAll
          translate={translate}
          value={values.types}
          onChange={(vls) =>
            this.setState(
              {
                values: {
                  ...values,
                  types: vls,
                  subtypes: [],
                },
                options: {
                  ...options,
                  subtypes: vls.length ? options.subtypes : [],
                },
              },
              () => {
                this.handleSelectChange(
                  5,
                  'subtypes',
                  null,
                  vls.map(({ value: { sectionId, subSectionId, typeId } }) => ({
                    sectionId,
                    subSectionId,
                    typeId,
                  }))
                )
              }
            )
          }
          fill
        />
        <Select
          label={translate('newSectionBlock.form.subtypeLabel')}
          placeholder={translate('newSectionBlock.form.subtypePlaceholder')}
          required
          validateValue={(value) => !!(value || []).length}
          options={options.subtypes.map((item) => ({
            value: item,
            label: item.description,
          }))}
          isMulti
          groupOptions
          selectAll
          translate={translate}
          value={values.subtypes}
          onChange={(vls) =>
            this.setState(
              { values: { ...values, subtypes: vls } },
              this.triggerChange
            )
          }
          fill
        />
      </form>
    )
  }
}

const mapStateToProps = (rootReducer) => ({
  ...(rootReducer || {}).sectionBlockReducer,
  permissions: rootReducer.menuReducer.permissions,
  auth: rootReducer.auth,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...sectionBlockActions }, dispatch)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    NewSectionBlockForm
  )
)

export { NewSectionBlockForm }
